import React, { useEffect, useState } from 'react';
import { Box, TextField, Typography, Select, MenuItem, IconButton, Button, Autocomplete } from '@mui/material';
import { Field, FieldArray, useFormikContext } from 'formik';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import DeleteIcon from '@mui/icons-material/Delete';
import dayjs from 'dayjs';
import { ncdAPI } from '../../../../services/ncd.api';

const CurrentMedicationForm = ({ individualId }) => {
  const { values, setFieldValue } = useFormikContext();
  const [loadedMedicines, setLoadedMedicines] = useState([]);
  const [loadedPrescriptions, setLoadedPrescriptions] = useState([]);
  const [medicineOptions, setMedicineOptions] = useState([]);
  const [medicineSearchText, setMedicineSearchText] = useState('');

  // Auto-calculate quantity based on frequency and duration
  useEffect(() => {
    const frequencyMap = {
      'Once a day': 1,
      'Twice a day': 2,
      'Thrice a day': 3,
      'Four times a day': 4
    };

    const durationUnitMap = {
      'Days': 1,
      'Months': 30,
      'Years': 365
    };

    if (values.medications) {
      values.medications.forEach((med, index) => {
        if (med.frequency && med.duration && med.durationUnit) {
          const freqValue = frequencyMap[med.frequency] || 0;
          const durationValue = parseInt(med.duration) || 0;
          const unitMultiplier = durationUnitMap[med.durationUnit] || 1;
          const calculatedQuantity = freqValue * durationValue * unitMultiplier;

          if (calculatedQuantity > 0 && med.quantity !== String(calculatedQuantity)) {
            setFieldValue(`medications.${index}.quantity`, String(calculatedQuantity));
          }
        }
      });
    }
  }, [values.medications, setFieldValue]);

  useEffect(() => {
    const fetchMedicineOptions = async () => {
      if (medicineSearchText && medicineSearchText.length >= 1) {
        try {
          const response = await ncdAPI.getInitialAssessmentMedicines(medicineSearchText);
          if (response && response.data && Array.isArray(response.data)) {
            const seen = new Set();
            const uniqueOptions = [];

            response.data.forEach(item => {
              const val = typeof item === 'string' ? item.trim() : item;
              if (val) {
                // Normalize key: lowercase and strip spaces, brackets, dashes, and punctuation
                const key = typeof val === 'string'
                  ? val.toLowerCase().replace(/[\s\(\)\[\]\{\}\-\_\,\.\/]+/g, '')
                  : JSON.stringify(val);

                if (!seen.has(key)) {
                  seen.add(key);
                  uniqueOptions.push(val);
                }
              }
            });

            setMedicineOptions(uniqueOptions);
          } else {
            setMedicineOptions([]);
          }
        } catch (error) {
          console.error('Error fetching medicine options:', error);
        }
      } else {
        setMedicineOptions([]);
      }
    };

    const debounceTimer = setTimeout(() => {
      fetchMedicineOptions();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [medicineSearchText]);

  useEffect(() => {
    const fetchMedications = async () => {
      try {
        const response = await ncdAPI.getInitialAssessmentMedications(individualId);
        if (response && response.data) {
          const medicines = response.data.medicines || [];
          const prescriptions = response.data.medicinesPrescribed || [];

          setLoadedMedicines(medicines.map(med => ({
            date: med.date || '',
            condition: med.conditn || '',
            conditionOther: med.conditnOthr || '',
            medicine: med.name || '',
            frequency: med.freq || '',
            duration: med.duratn || '',
            durationUnit: 'Days',
            quantity: String(med.quan || ''),
            instruction: med.spInst || '',
            medicineSourceType: med.medicineSourceType,
            isLoaded: true,
            isDeletable: true
          })));

          setLoadedPrescriptions(prescriptions.map(med => ({
            date: med.date || '',
            condition: med.conditn || '',
            conditionOther: med.conditnOthr || '',
            medicine: med.name || '',
            frequency: med.freq || '',
            duration: med.duratn || '',
            durationUnit: 'Days',
            quantity: String(med.quan || ''),
            instruction: med.spInst || '',
            medicineSourceType: med.medicineSourceType,
            lastUpdatedBy: med.lastUpdatedBy,
            lastUpdatedFacility: med.lastUpdatedFacility,
            isLoaded: true,
            isDeletable: false
          })));
        }
      } catch (error) {
        console.error('Error fetching medications:', error);
      }
    };

    if (individualId) {
      fetchMedications();
    }
  }, [individualId]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box className="form-section">
        <Typography variant="h6" className="section-title" sx={{ mb: 2 }}>Current Medication</Typography>

        <Field name="currentMedicationDate">
          {({ field }) => (
            <DatePicker
              value={field.value ? dayjs(field.value) : dayjs()}
              onChange={(newValue) => setFieldValue('currentMedicationDate', newValue)}
              format="DD-MM-YYYY"
              maxDate={dayjs()}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { mb: 2, width: '200px' }
                }
              }}
            />
          )}
        </Field>

        <Box sx={{
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          overflow: 'hidden'
        }}>
          {/* Table Header */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: '1.2fr 2fr 1.5fr 0.8fr 1fr 0.8fr 1.5fr 60px',
            gap: 0,
            bgcolor: '#f5f5f9',
            borderBottom: '1px solid #e0e0e0'
          }}>
            <Box sx={{ p: 1.5, borderRight: '1px solid #e0e0e0', fontWeight: 600, fontSize: '14px' }}>
              Condition <span style={{ color: 'red' }}>*</span>
            </Box>
            <Box sx={{ p: 1.5, borderRight: '1px solid #e0e0e0', fontWeight: 600, fontSize: '14px' }}>
              Medicine <span style={{ color: 'red' }}>*</span>
            </Box>
            <Box sx={{ p: 1.5, borderRight: '1px solid #e0e0e0', fontWeight: 600, fontSize: '14px' }}>
              Frequency <span style={{ color: 'red' }}>*</span>
            </Box>
            <Box sx={{ p: 1.5, borderRight: '1px solid #e0e0e0', fontWeight: 600, fontSize: '14px' }}>

            </Box>
            <Box sx={{ p: 1.5, borderRight: '1px solid #e0e0e0', fontWeight: 600, fontSize: '14px', textAlign: 'center' }}>
              Duration <span style={{ color: 'red' }}>*</span>
            </Box>
            <Box sx={{ p: 1.5, borderRight: '1px solid #e0e0e0', fontWeight: 600, fontSize: '14px' }}>
              Quantity
            </Box>
            <Box sx={{ p: 1.5, borderRight: '1px solid #e0e0e0', fontWeight: 600, fontSize: '14px' }}>
              Special Instruction
            </Box>
            <Box sx={{ p: 1.5 }}></Box>
          </Box>

          {/* Loaded Medicines (deletable) */}
          {loadedMedicines.map((med, index) => (
            <Box key={`loaded-${index}`} sx={{
              display: 'grid',
              gridTemplateColumns: '1.2fr 2fr 1.5fr 0.8fr 1fr 0.8fr 1.5fr 60px',
              gap: 0,
              borderBottom: '1px solid #e0e0e0',
              '&:last-child': { borderBottom: 'none' }
            }}>
              <Box sx={{ p: 1, borderRight: '1px solid #e0e0e0' }}>
                <TextField value={med.condition} disabled size="small" fullWidth sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)' } }} />
                {med.condition === 'Other' && med.conditionOther && (
                  <TextField value={med.conditionOther} disabled size="small" fullWidth sx={{ mt: 1, '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)' } }} />
                )}
              </Box>
              <Box sx={{ p: 1, borderRight: '1px solid #e0e0e0' }}>
                <TextField value={med.medicine} disabled size="small" fullWidth sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)' } }} />
              </Box>
              <Box sx={{ p: 1, borderRight: '1px solid #e0e0e0' }}>
                <TextField value={med.frequency} disabled size="small" fullWidth sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)' } }} />
              </Box>
              <Box sx={{ p: 1, borderRight: '1px solid #e0e0e0' }}>
                <TextField value={med.duration} disabled size="small" fullWidth sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)' } }} />
              </Box>
              <Box sx={{ p: 1, borderRight: '1px solid #e0e0e0' }}>
                <TextField value={med.durationUnit || 'Days'} disabled size="small" fullWidth sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)' } }} />
              </Box>
              <Box sx={{ p: 1, borderRight: '1px solid #e0e0e0' }}>
                <TextField value={med.quantity} disabled size="small" fullWidth sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)' } }} />
              </Box>
              <Box sx={{ p: 1, borderRight: '1px solid #e0e0e0' }}>
                <TextField value={med.instruction} disabled size="small" fullWidth sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)' } }} />
              </Box>
              <Box sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <IconButton
                  onClick={() => setLoadedMedicines(prev => prev.filter((_, i) => i !== index))}
                  size="small"
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>
          ))}

          {/* Loaded Prescriptions (not deletable) */}
          {loadedPrescriptions.map((med, index) => (
            <Box key={`prescription-${index}`} sx={{
              display: 'grid',
              gridTemplateColumns: '1.2fr 2fr 1.5fr 0.8fr 1fr 0.8fr 1.5fr 60px',
              gap: 0,
              borderBottom: '1px solid #e0e0e0',
              '&:last-child': { borderBottom: 'none' }
            }}>
              <Box sx={{ p: 1, borderRight: '1px solid #e0e0e0' }}>
                <TextField value={med.condition} disabled size="small" fullWidth sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)' } }} />
                {med.condition === 'Other' && med.conditionOther && (
                  <TextField value={med.conditionOther} disabled size="small" fullWidth sx={{ mt: 1, '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)' } }} />
                )}
              </Box>
              <Box sx={{ p: 1, borderRight: '1px solid #e0e0e0' }}>
                <TextField value={med.medicine} disabled size="small" fullWidth sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)' } }} />
              </Box>
              <Box sx={{ p: 1, borderRight: '1px solid #e0e0e0' }}>
                <TextField value={med.frequency} disabled size="small" fullWidth sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)' } }} />
              </Box>
              <Box sx={{ p: 1, borderRight: '1px solid #e0e0e0' }}>
                <TextField value={med.duration} disabled size="small" fullWidth sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)' } }} />
              </Box>
              <Box sx={{ p: 1, borderRight: '1px solid #e0e0e0' }}>
                <TextField value={med.durationUnit || 'Days'} disabled size="small" fullWidth sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)' } }} />
              </Box>
              <Box sx={{ p: 1, borderRight: '1px solid #e0e0e0' }}>
                <TextField value={med.quantity} disabled size="small" fullWidth sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)' } }} />
              </Box>
              <Box sx={{ p: 1, borderRight: '1px solid #e0e0e0' }}>
                <TextField value={med.instruction} disabled size="small" fullWidth sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)' } }} />
              </Box>
              <Box sx={{ p: 1 }}></Box>
            </Box>
          ))}

          {/* New medications from form */}
          <FieldArray name="medications">
            {({ push, remove }) => (
              <>
                {values.medications?.map((med, index) => {
                  const hasCondition = med.condition && med.condition !== '';
                  const hasMedicine = med.medicine && med.medicine !== '';

                  return (
                    <Box key={`new-${index}`} sx={{
                      display: 'grid',
                      gridTemplateColumns: '1.2fr 2fr 1.5fr 0.8fr 1fr 0.8fr 1.5fr 60px',
                      gap: 0,
                      borderBottom: '1px solid #e0e0e0',
                      '&:last-child': { borderBottom: 'none' }
                    }}>
                      <Box sx={{ p: 1, borderRight: '1px solid #e0e0e0' }}>
                        <Field name={`medications.${index}.condition`}>
                          {({ field }) => (
                            <Select
                              {...field}
                              displayEmpty
                              size="small"
                              fullWidth
                              sx={{ fontSize: '12px' }}
                              onChange={(e) => {
                                field.onChange(e);
                                if (e.target.value !== 'Other') {
                                  setFieldValue(`medications.${index}.conditionOther`, '');
                                }
                              }}
                            >
                              <MenuItem value="" sx={{ fontSize: '12px' }}>Select</MenuItem>
                              <MenuItem value="Hypertension" sx={{ fontSize: '12px' }}>Hypertension</MenuItem>
                              <MenuItem value="Diabetes" sx={{ fontSize: '12px' }}>Diabetes</MenuItem>
                              <MenuItem value="General" sx={{ fontSize: '12px' }}>General</MenuItem>
                              <MenuItem value="Cholesterol" sx={{ fontSize: '12px' }}>Cholesterol</MenuItem>
                              <MenuItem value="Other" sx={{ fontSize: '12px' }}>Other</MenuItem>
                            </Select>
                          )}
                        </Field>
                        {med.condition === 'Other' && (
                          <Field name={`medications.${index}.conditionOther`}>
                            {({ field }) => (
                              <TextField
                                {...field}
                                size="small"
                                fullWidth
                                placeholder="Specify other condition"
                                sx={{ mt: 1, '& .MuiInputBase-input': { fontSize: '12px' } }}
                              />
                            )}
                          </Field>
                        )}
                      </Box>
                      <Box sx={{ p: 1, borderRight: '1px solid #e0e0e0' }}>
                        {hasCondition ? (
                          <Field name={`medications.${index}.medicine`}>
                            {({ field, form }) => (
                              <Autocomplete
                                options={medicineOptions}
                                value={field.value || null}
                                onChange={(event, newValue) => {
                                  form.setFieldValue(`medications.${index}.medicine`, newValue || '');
                                }}
                                onInputChange={(event, newInputValue) => {
                                  setMedicineSearchText(newInputValue);
                                }}
                                freeSolo
                                componentsProps={{
                                  popper: {
                                    sx: {
                                      '& .MuiAutocomplete-option': {
                                        fontSize: '15px',
                                        padding: '4px 8px',
                                      },
                                    },
                                  },
                                }}
                                slotProps={{
                                  popper: {
                                    sx: {
                                      '& .MuiAutocomplete-option': {
                                        fontSize: '15px',
                                        padding: '4px 8px',
                                      },
                                    },
                                  },
                                }}
                                renderOption={(props, option) => {
                                  const { key, ...optionProps } = props;
                                  return (
                                    <li key={key} {...optionProps} style={{ fontSize: '15px' }}>
                                      {option}
                                    </li>
                                  );
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    size="small"
                                    placeholder="Search medicine..."
                                    sx={{
                                      '& .MuiInputBase-input': { fontSize: '15px' }
                                    }}
                                  />
                                )}
                              />
                            )}
                          </Field>
                        ) : (
                          <TextField
                            disabled
                            size="small"
                            fullWidth
                            placeholder="Select condition first"
                            sx={{ '& .MuiInputBase-input': { fontSize: '15px' } }}
                          />
                        )}
                      </Box>
                      <Box sx={{ p: 1, borderRight: '1px solid #e0e0e0' }}>
                        <Field name={`medications.${index}.frequency`}>
                          {({ field }) => (
                            <Select {...field} displayEmpty size="small" fullWidth disabled={!hasMedicine} sx={{ fontSize: '12px' }}>
                              <MenuItem value="" sx={{ fontSize: '15px' }}>Select</MenuItem>
                              <MenuItem value="Once a day" sx={{ fontSize: '15px' }}>Once a day</MenuItem>
                              <MenuItem value="Twice a day" sx={{ fontSize: '15px' }}>Twice a day</MenuItem>
                              <MenuItem value="Thrice a day" sx={{ fontSize: '15px' }}>Thrice a day</MenuItem>
                              <MenuItem value="Four times a day" sx={{ fontSize: '15px' }}>Four times a day</MenuItem>
                            </Select>
                          )}
                        </Field>
                      </Box>
                      <Box sx={{ p: 1, borderRight: '1px solid #e0e0e0' }}>
                        <Field name={`medications.${index}.duration`}>
                          {({ field }) => (
                            <TextField {...field} size="small" fullWidth placeholder="2" disabled={!hasMedicine} sx={{ '& .MuiInputBase-input': { fontSize: '12px' } }} />
                          )}
                        </Field>
                      </Box>
                      <Box sx={{ p: 1, borderRight: '1px solid #e0e0e0' }}>
                        <Field name={`medications.${index}.durationUnit`}>
                          {({ field }) => (
                            <Select {...field} size="small" fullWidth value={field.value || 'Days'} disabled={!hasMedicine} sx={{ fontSize: '12px' }}>
                              <MenuItem value="Days" sx={{ fontSize: '12px' }}>Days</MenuItem>
                              <MenuItem value="Months" sx={{ fontSize: '12px' }}>Months</MenuItem>
                              <MenuItem value="Years" sx={{ fontSize: '12px' }}>Years</MenuItem>
                            </Select>
                          )}
                        </Field>
                      </Box>
                      <Box sx={{ p: 1, borderRight: '1px solid #e0e0e0' }}>
                        <Field name={`medications.${index}.quantity`}>
                          {({ field }) => (
                            <TextField {...field} size="small" fullWidth disabled sx={{ '& .MuiInputBase-input': { fontSize: '12px' } }} />
                          )}
                        </Field>
                      </Box>
                      <Box sx={{ p: 1, borderRight: '1px solid #e0e0e0' }}>
                        <Field name={`medications.${index}.instruction`}>
                          {({ field }) => (
                            <Select {...field} displayEmpty size="small" fullWidth disabled={!hasMedicine} sx={{ fontSize: '12px' }}>
                              <MenuItem value="" sx={{ fontSize: '12px' }}>Select</MenuItem>
                              <MenuItem value="After Food" sx={{ fontSize: '12px' }}>After Food</MenuItem>
                              <MenuItem value="Before Food" sx={{ fontSize: '12px' }}>Before Food</MenuItem>
                            </Select>
                          )}
                        </Field>
                      </Box>
                      <Box sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <IconButton onClick={() => remove(index)} size="small" color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  );
                })}
              </>
            )}
          </FieldArray>
        </Box>

        <FieldArray name="medications">
          {({ push }) => (
            <Button
              onClick={() => push({ condition: '', conditionOther: '', medicine: '', frequency: '', duration: '', durationUnit: 'Days', quantity: '', instruction: '' })}
              variant="contained"
              sx={{ mt: 2, bgcolor: '#009688', '&:hover': { bgcolor: '#00796b' }, textTransform: 'none', fontWeight: 600 }}
            >
              + MEDICINE
            </Button>
          )}
        </FieldArray>
      </Box>
    </LocalizationProvider>
  );
};

export default CurrentMedicationForm;
